/* eslint-disable no-param-reassign, padded-blocks */
/* This mixin contains the event handlers for the Project Map object */

import { loadModules } from 'esri-loader';

export default {
  methods: {
    async addBasemaps(basemapsConfig) {
      await loadModules(['esri/Basemap', 'esri/layers/TileLayer', 'esri/Map', 'esri/WebMap']).then(async ([Basemap, TileLayer, Map, WebMap]) => {

        this.maps = [];

        basemapsConfig.forEach((item) => {
          switch (item.class) {
            case 'Map':
              this.maps.push(new Map(item.object));
              break;
            case 'TileLayerBaseMap': {
              const tileLayer = new TileLayer(item.object);
              const basemap = new Basemap({
                baseLayers: [tileLayer],
              });
              this.maps.push(new Map({
                title: item.object.title,
                basemap,
              }));
              break;
            }
            case 'WebMap': {
              const webMap = new WebMap(item.object);
              webMap.allLayers.on('change', () => {
                const targetLayer = webMap.allLayers.items.find((layer) => layer.id === 'World_Transportation_5177');
                if (targetLayer) {
                  targetLayer.visible = true;
                }
              });
              this.maps.push(webMap);
              break;
            }
            default:
              break;
          }
        });
      });
    },
    addButtons() {
      if (this.isEditable) {
        [
          this.$refs.btnDefaultButtons,
          this.$refs.btnManageSitesButtons,
          this.$refs.btnEditButtons,
        ].forEach((button) => this.view.ui.add(button, 'bottom-right'));
      }
    },
    async addLayerList() {
      await loadModules(['esri/widgets/Expand', 'esri/widgets/LayerList']).then(async ([Expand, LayerList]) => {

        this.layerList = new LayerList({
          container: document.createElement('div'),
          view: this.view,
        });

        this.layerListExpand = new Expand({
          expandIconClass: 'esri-icon-layer-list',
          expandTooltip: 'Expand Layers',
          group: 'legend-buttons',
          view: this.view,
          content: this.layerList,
        });

        this.view.ui.add(this.layerListExpand, { position: 'top-right' });
      });
    },
    async addLayers(layersConfig) {
      await loadModules(['esri/layers/FeatureLayer', 'esri/layers/GraphicsLayer']).then(async ([FeatureLayer, GraphicsLayer]) => {

        this.layers = [];

        layersConfig.forEach(
          (referenceLayer) => this.layers.push(new FeatureLayer(referenceLayer)),
        );

        this.huc10Layer = this.layers.find((layer) => layer.id === 'huc10FeatureLayer');
        this.huc12Layer = this.layers.find((layer) => layer.id === 'huc12FeatureLayer');
        this.taxlotLayer = this.layers.find((layer) => layer.id === 'taxlotFeatureLayer');
        this.siteLayer = this.layers.find((layer) => layer.id === 'siteFeatureLayer');
        this.projectLayer = this.layers.find((layer) => layer.id === 'projectFeatureLayer');

        this.graphicsLayer = new GraphicsLayer();
        this.graphicsLayer.title = this.project.projectName;
        this.layers.push(this.graphicsLayer);

        this.geocodeLayer = new GraphicsLayer();
        this.geocodeLayer.title = 'ConsEd Project Geocode Results';
        this.layers.push(this.geocodeLayer);

        this.layers.forEach((layer) => this.view.map.add(layer));
      });
    },
    addLegend() {
      loadModules(['esri/widgets/Expand', 'esri/widgets/Legend']).then(([Expand, Legend]) => {

        this.legendExpand = new Expand({
          content: new Legend({
            view: this.view,
          }),
          expandIconClass: 'esri-icon-maps',
          expandTooltip: 'Expand Legend',
          group: 'legend-buttons',
          view: this.view,
        });

        this.view.ui.add(this.legendExpand, 'top-right');
      });
    },
    async addMapPicker() {
      await loadModules(['esri/widgets/Expand']).then(async ([Expand]) => {

        this.webmapList = this.$refs.portalItemList;

        this.webmapListExpand = new Expand({
          expandIconClass: 'esri-icon-basemap',
          expandTooltip: 'Expand Basemaps',
          group: 'legend-buttons',
          view: this.view,
          content: this.webmapList,
        });

        this.view.ui.add(this.webmapListExpand, { position: 'top-right' });
      });
    },
    addSelectLayerList() {
      loadModules(['esri/widgets/Expand']).then(async ([Expand]) => {

        this.selectLayer = null;
        this.selectLayerItem = -1;

        this.selectLayerListExpand = new Expand({
          content: this.$refs.selectLayerItemList,
          expandIconClass: 'esri-icon-layers',
          expandTooltip: 'Expand Selection Layers',
          group: 'legend-buttons',
          view: this.view,
        });

        this.view.ui.add(this.selectLayerListExpand, { position: 'top-right' });
      });
    },
    getProjectWatersheds(projectGeometry, featureLayer) {
      return new Promise((resolve, reject) => {
        loadModules([
          'esri/geometry/projection',
          'esri/geometry/SpatialReference',
        ]).then(async ([projection, SpatialReference]) => {
          projection.load().then(() => {

            if (!projectGeometry.spatialReference.isWGS84) {
              projectGeometry = projection.project(
                projectGeometry,
                SpatialReference.WGS84,
              );
            }

            const query = featureLayer.createQuery();
            query.geometry = projectGeometry;
            query.spatialRelationship = 'intersects';
            query.returnGeometry = true;
            query.outFields = ['*'];

            featureLayer.queryFeatures(query).then((result) => {

              const huc12Watersheds = [];

              result.features.forEach((watershed) => {
                const area = this.geometryEngine.geodesicArea(this.geometryEngine.intersect(projectGeometry, watershed.geometry), 'square-meters');
                huc12Watersheds.push({
                  watershedId: watershed.attributes.huc10,
                  watershedName: watershed.attributes.huc10_name,
                  subwatershedId: watershed.attributes.huc12,
                  subwatershedName: watershed.attributes.huc12_name,
                  area,
                });
              });

              huc12Watersheds.sort((a, b) => (a.area - b.area));

              const item = huc12Watersheds[huc12Watersheds.length - 1];

              resolve({
                watershedId: item.watershedId,
                watershedName: item.watershedName,
                subwatershedIds: [item.subwatershedId],
                subwatershedNames: [item.subwatershedName],
              });

            }).catch((exception) => {
              console.error('Enter queryFeatures catch()');
              console.error(exception);
              reject(exception);
              console.error('Exit queryFeatures catch()');
            });
          });
        }).catch((exception) => {
          console.error('Enter loadModules catch()');
          console.error(exception);
          reject(exception);
          console.error('Exit loadModules catch()');
        });
      });
    },
    async loadMap() {
      loadModules(
        [
          'esri/geometry/geometryEngine',
          'esri/Graphic',
          'esri/identity/IdentityManager',
          'esri/views/draw/Draw',
          'esri/views/MapView',
          'esri/widgets/Home',
          'esri/widgets/Sketch',
        ],
        { css: true },
      ).then(async ([
        geometryEngine,
        Graphic,
        IdentityManager,
        Draw,
        MapView,
        Home,
        Sketch,
      ]) => {

        this.geometryEngine = geometryEngine;
        this.Graphic = Graphic;
        this.identityManager = IdentityManager;
        this.Draw = Draw;
        this.Home = Home;
        this.Sketch = Sketch;

        if (localStorage['acorn.identityManager']) {
          try {
            this.identityManager.initialize(localStorage['acorn.identityManager']);
          } catch (exception) {
            console.error(exception);
            console.error('this.identityManager could not be initialized with localStorage[\'acorn.identityManager\']]');
          }
        }

        this.identityManager.on('credential-create', () => {
          try {
            localStorage['acorn.identityManager'] = JSON.stringify(this.identityManager.toJSON());
          } catch (exception) {
            console.error(exception);
            console.error('localStorage[\'acorn.identityManager\'] could not be set with JSON.stringify(this.identityManager.toJSON())');
          }
        });

        await this.addBasemaps(this.config.projectMap.basemaps);

        this.view = new MapView({
          container: 'esriMap',
          map: this.maps[0],
          center: this.config.projectMap.defaultExtent.center,
          zoom: this.config.projectMap.defaultExtent.zoom,
        });

        this.view.ui.move('zoom', 'top-right');

        await this.addLayers(this.config.projectMap.layers);
        await this.addLayerList();
        await this.addMapPicker();

        this.addLegend();
        this.addButtons();

        this.onProjectGeometrySet();
      });
    },
    loadProjectGraphic(projectGeometry) {
      this.graphicsLayer.removeAll();
      const graphic = new this.Graphic({
        geometry: projectGeometry,
        symbol: this.config.projectMap.projectDisplaySymbol,
      });
      this.graphicsLayer.add(graphic);
    },
    removeSelectLayerHitHighlights() {
      this.selectLayerHits.forEach((item) => {
        item.highlight.remove();
      });
      this.selectLayerHits = [];
    },
    removeSelectLayerList() {
      this.view.ui.remove(this.selectLayerListExpand);
    },
    removeSelectLayerListLayer() {
      const layer = this.view.map.findLayerById(this.selectLayerId);
      if (layer) {
        this.view.map.layers.remove(layer);
      }
    },
    saveProjectGeometry() {

      const projectGeometry = this.geometryEngine.union(
        this.graphicsLayer.graphics.items.map((item) => item.geometry),
      );

      this.graphicsLayer.removeAll();

      this.$store.dispatch('Project/setProjectGeometry', projectGeometry).then(() => {

        this.getProjectWatersheds(
          projectGeometry,
          this.huc12Layer,
        ).then(async (projectWatersheds) => {

          this.project.watershedId = projectWatersheds.watershedId;
          this.project.watershedName = projectWatersheds.watershedName;
          this.project.subwatershedIds = projectWatersheds.subwatershedIds;
          this.project.subwatershedNames = projectWatersheds.subwatershedNames;

          await this.$store.dispatch('Project/setProjectAttributes', this.project).then(() => {
            if (this.errorMessage) {
              this.showErrorMessageAlertBody = `Saving project data failed with the following message: '${this.errorMessage}'`;
              this.showErrorMessageAlert = true;
            }
          }).catch((exception) => {
            console.error('Enter Project/setProjectAttributes catch()');
            console.error(exception);
            console.error('Exit Project/setProjectAttributes catch()');
          });

        }).catch((exception) => {
          console.error('Enter getProjectWatersheds catch()');
          console.error(exception);
          console.error('Exit getProjectWatersheds catch()');
        });

        this.upsertProjectGeometry(this.project, this.projectGeometry).then(() => {
          this.loadProjectGraphic(this.projectGeometry);
          this.setExtentByProjectGeometry(this.projectGeometry);
          this.btnDefaultButtonsShow = true;
        }).catch((exception) => {
          console.error('Enter upsertProjectGeometry catch callback');
          console.error(exception);
          console.error('Exit upsertProjectGeometry catch callback');
        });
      });
    },
    saveProjectSites() {
      return new Promise((resolve, reject) => {

        const selectedSiteIds = this.selectedSites.map(
          (selectedSite) => selectedSite.graphic.attributes.insightlyid,
        );

        const deletedProjectSites = this.projectSites.filter(
          (projectSite) => !(selectedSiteIds.includes(projectSite.relatedSiteId)),
        );

        const deletedProjectSiteIds = deletedProjectSites.map(
          (deletedProjectSite) => deletedProjectSite.id,
        );

        const projectSiteRelatedSiteIds = this.projectSites.map(
          (projectSite) => projectSite.relatedSiteId,
        );

        const addedSites = this.selectedSites.filter(
          (selectedSite) => !(projectSiteRelatedSiteIds.includes(
            selectedSite.graphic.attributes.insightlyid,
          )),
        ).map((addedSite) => addedSite.graphic);

        this.$store.dispatch('Project/deleteProjectSites', deletedProjectSiteIds).then(() => {
          if (!this.errorMessage) {
            this.$store.dispatch('Project/setProjectSites', { projectId: this.project.id, projectSites: addedSites }).then(() => {
              if (!this.errorMessage) {
                resolve();
              } else {
                this.showErrorMessageAlertBody = `Saving project sites failed with the following message: '${this.errorMessage}'`;
                this.showErrorMessageAlert = true;
                reject(this.showErrorMessageAlertBody);
              }
            }).catch((exception) => {
              console.error('Enter Project/setProjectSites catch()');
              console.error(exception);
              reject(exception);
              console.error('Exit Project/setProjectSites catch()');
            });
          } else {
            this.showErrorMessageAlertBody = `Deleting project sites failed with the following message: '${this.errorMessage}'`;
            this.showErrorMessageAlert = true;
            reject(this.showErrorMessageAlertBody);
          }
        }).catch((exception) => {
          console.error('Enter Project/deleteProjectSites catch()');
          console.error(exception);
          reject(exception);
          console.error('Exit Project/deleteProjectSites catch()');
        });
      });
    },
    setExtentByProjectAddress(project) {
      loadModules(['esri/tasks/Locator']).then(([Locator]) => {
        this.view.whenLayerView(this.projectLayer).then(() => {

          this.geocodeLayer.removeAll();

          this.locator = new Locator({
            outSpatialReference: { wkid: 102100 },
            url: this.config.projectMap.locator.url,
          });

          let singleLine = (`${(project.consEdStreet || '')} ${(project.consEdCity || '')} ${(project.consEdZip || '')} OR`).trim();
          while (singleLine.includes('  ')) {
            singleLine = singleLine.replace('  ', ' ');
          }

          this.locator.addressToLocations({
            address: {
              SingleLine: singleLine,
            },
            countryCode: 'US',
            forStorage: false,
          }).then((addressCandidates) => {

            const candidates = addressCandidates || [];

            if (
              candidates.length > 0
              && candidates[0].score >= this.config.projectMap.locator.tolerance
            ) {

              const candidate = candidates[0];

              this.view.extent = this.geometryEngine.geodesicBuffer(candidate.extent, 1000, 'meters').extent;

              const graphic = new this.Graphic({
                geometry: candidate.location,
                symbol: this.config.projectMap.locator.markerSymbol,
              });

              this.geocodeLayer.add(graphic);

              const textGraphicSymbol = this.config.projectMap.locator.markerLabel;
              textGraphicSymbol.text = candidate.address;

              const textGraphic = new this.Graphic({
                geometry: candidate.location,
                symbol: textGraphicSymbol,
              });

              this.geocodeLayer.add(textGraphic);

              this.showGeocodeSuccess();

            } else {
              this.view.center = this.config.projectMap.defaultExtent.center;
              this.view.zoom = this.config.projectMap.defaultExtent.zoom;
              this.showGeocodeWarning();
            }

            if (this.homeButton) {
              this.view.ui.remove(this.homeButton);
            }
            this.homeButton = new this.Home({
              view: this.view,
            });
            this.view.ui.add({
              component: this.homeButton,
              position: 'top-right',
              index: 1,
            });
          });
        });
      });
    },
    setExtentByProjectGeometry(projectGeometry) {
      this.view.whenLayerView(this.projectLayer).then(() => {
        this.view.extent = this.geometryEngine.geodesicBuffer(projectGeometry, 1000, 'meters').extent;
        if (this.homeButton) {
          this.view.ui.remove(this.homeButton);
        }
        this.homeButton = new this.Home({
          view: this.view,
        });
        this.view.ui.add({
          component: this.homeButton,
          position: 'top-right',
          index: 1,
        });
      });
    },
    setExtentByDefault() {
      this.view.whenLayerView(this.projectLayer).then(() => {
        if (this.homeButton) {
          this.view.ui.remove(this.homeButton);
        }
        this.homeButton = new this.Home({
          view: this.view,
        });
        this.view.ui.add({
          component: this.homeButton,
          position: 'top-right',
          index: 1,
        });
      });
    },
    upsertProjectGeometry(project, projectGeometry) {
      return new Promise((resolve, reject) => {

        this.view.whenLayerView(this.projectLayer).then(() => {

          const layer = this.projectLayer;

          const query = layer.createQuery();
          query.where = `insightlyid='${project.id}'`;

          layer.queryFeatures(query).then((results) => {

            const applyEditsParameter = {};

            if (results.features.length === 0) {
              applyEditsParameter.addFeatures = [
                new this.Graphic({
                  geometry: projectGeometry,
                  attributes: {
                    insightlyid: project.id,
                    projectname: project.projectName,
                  },
                }),
              ];
            } else {
              applyEditsParameter.updateFeatures = results.features.map(
                (feature) => new this.Graphic(
                  { geometry: projectGeometry, attributes: feature.attributes },
                ),
              );
            }

            layer.applyEdits(applyEditsParameter).then(() => {
              resolve();
            }).catch((applyEditsException) => {
              console.error('Enter upsertProjectGeometry applyEdits catch()');
              console.error('applyEditsException:', applyEditsException);
              reject(applyEditsException);
              console.error('Exit upsertProjectGeometry applyEdits catch()');
            });
          }).catch((queryFeaturesException) => {
            console.error('Enter upsertProjectGeometry queryFeatures catch()');
            console.error('queryFeaturesException: ', queryFeaturesException);
            reject(queryFeaturesException);
            console.error('Exit upsertProjectGeometry queryFeatures catch()');
          });
        });
      });
    },
  },
};
